import { useState, useEffect, useCallback } from "react";
import styles from "./AdminForm.module.css";
import apiService from "../../../../services/api";
import { validateFile, createImagePreview, revokeImagePreview } from "../../../../utils/fileUpload";

export default function SeminarForm({ item, onSubmit, onCancel }) {
    const [formData, setFormData] = useState({
        title: "",
        content: "",
        imageFile: null,
        date: new Date().toISOString().split('T')[0]
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [fileError, setFileError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (item) {
            setFormData({
                title: item.title || "",
                content: item.content || "",
                imageFile: null,
                date: item.date ? new Date(item.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            });
            if (item.imageUrl) {
                setImagePreview(item.imageUrl);
            }
        }
    }, [item]);

    useEffect(() => {
        return () => {
            if (imagePreview && imagePreview !== item?.imageUrl) {
                revokeImagePreview(imagePreview);
            }
        };
    }, [imagePreview, item?.imageUrl]);

    const handleImageChange = useCallback((e) => {
        const file = e.target.files[0];
        if (!file) return;

        const validation = validateFile(file);
        if (!validation.isValid) {
            setFileError(validation.errors.join(', '));
            return;
        }

        setFileError("");
        setFormData(prev => ({ ...prev, imageFile: file }));
        
        const preview = createImagePreview(file);
        setImagePreview(preview);
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        if (isSubmitting) {
            console.log('Form submission already in progress, ignoring duplicate submit');
            return;
        }
        
        if (fileError) {
            alert('이미지 파일을 확인해주세요: ' + fileError);
            return;
        }

        setIsSubmitting(true);
        
        try {
            console.log('Submitting form data:', formData);
            
            if (item && item.id) {
                await apiService.updateSeminar(item.id, formData);
            } else {
                await apiService.createSeminar(formData);
            }
            alert("저장되었습니다.");
            onSubmit();
        } catch (error) {
            console.error('Form submission error:', error);
            alert(`저장에 실패했습니다: ${error.message || '알 수 없는 오류'}`);
        } finally {
            setIsSubmitting(false);
        }
    }, [formData, item, onSubmit, fileError, isSubmitting]);

    const handleCancel = useCallback(() => {
        if (isSubmitting) return;
        onCancel();
    }, [onCancel, isSubmitting]);

    return (
        <form className={styles.formWrap} onSubmit={handleSubmit}>
            <h2 className={styles.title}>{item ? "세미나 & 컨퍼런스 수정" : "세미나 & 컨퍼런스 등록"}</h2>
            
            <label className={styles.label}>
                제목
                <input 
                    type="text" 
                    className={styles.input} 
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                    required 
                    disabled={isSubmitting}
                />
            </label>
            
            <label className={styles.label}>
                날짜
                <input 
                    type="date" 
                    className={styles.input} 
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({...prev, date: e.target.value}))}
                    required 
                    disabled={isSubmitting}
                />
            </label>
            
            <label className={styles.label}>
                내용
                <textarea 
                    className={styles.textarea} 
                    value={formData.content}
                    onChange={(e) => setFormData(prev => ({...prev, content: e.target.value}))}
                    required 
                    disabled={isSubmitting}
                />
            </label>
            
            <label className={styles.label}>
                이미지
                <div className={styles.imageUpload}>
                    <input
                        id="imageInput"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={isSubmitting}
                    />
                    <div className={styles.imageUploadIcon}>📷</div>
                    <div className={styles.imageUploadText}>이미지를 선택하세요</div>
                    <div className={styles.imageUploadHint}>JPG, PNG, GIF, SVG 파일 지원</div>
                </div>
                {fileError && <div className={styles.error}>{fileError}</div>}
                {imagePreview && (
                    <div className={styles.imagePreview}>
                        <img 
                            src={imagePreview} 
                            alt="미리보기" 
                        />
                    </div>
                )}
            </label>
            
            <div className={styles.buttonGroup}>
                <button 
                    type="submit" 
                    className={styles.submitBtn}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? "처리중..." : (item ? "수정" : "등록")}
                </button>
                <button 
                    type="button" 
                    onClick={handleCancel} 
                    className={styles.cancelBtn}
                    disabled={isSubmitting}
                >
                    취소
                </button>
            </div>
        </form>
    );
}

